import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as mediaApi from '@app/api/media.api';
import { Media, MediaFilters, MediaPaginatedResponse } from '@app/types/api.types';

export interface MediaState {
  items: Media[];
  currentItem: Media | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: MediaFilters;
  loading: boolean;
  error: string | null;
}

const initialState: MediaState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 20,
  },
  loading: false,
  error: null,
};

export const fetchMedia = createAsyncThunk(
  'media/fetchMedia',
  async (filters: MediaFilters = { page: 1, limit: 20 }, { rejectWithValue }) => {
    try {
      return await mediaApi.getMedia(filters);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch media');
    }
  }
);

export const fetchMediaById = createAsyncThunk(
  'media/fetchMediaById',
  async (id: string, { rejectWithValue }) => {
    try {
      return await mediaApi.getMediaById(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch media');
    }
  }
);

export const uploadMedia = createAsyncThunk(
  'media/uploadMedia',
  async ({ file, alt }: { file: File; alt?: string }, { rejectWithValue }) => {
    try {
      return await mediaApi.uploadFile(file, alt);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload media');
    }
  }
);

export const deleteMedia = createAsyncThunk(
  'media/deleteMedia',
  async (id: string, { rejectWithValue }) => {
    try {
      await mediaApi.deleteMedia(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete media');
    }
  }
);

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<MediaFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Media
    builder.addCase(fetchMedia.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMedia.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit),
        hasNext: action.payload.hasNext || action.payload.page < (action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit)),
        hasPrev: action.payload.hasPrev,
      };
    });
    builder.addCase(fetchMedia.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Media By ID
    builder.addCase(fetchMediaById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMediaById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchMediaById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Upload Media
    builder.addCase(uploadMedia.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(uploadMedia.fulfilled, (state, action) => {
      state.loading = false;
      state.items.unshift(action.payload);
    });
    builder.addCase(uploadMedia.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Media
    builder.addCase(deleteMedia.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteMedia.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteMedia.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setFilters, clearCurrentItem, clearError } = mediaSlice.actions;
export default mediaSlice.reducer;

